import Quill from 'quill';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { PlusIcon, RedoIcon, UndoIcon } from '@/components/Icons';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import { useLogger } from '../log';
import './cursorPlaceholderBlot';
import './mergeFieldBlot';
import { plainTextToQuillHtml, quillHtmlToPlainText } from './plainTextHtml';
import {
    containsSignature,
    insertSignatureAtEnd,
    isEmptyHtml,
} from './signatureUtils';

type UseQuillOptions = {
    placeholder?: string;
    id?: string;
    signature?: string;
    initialValue?: string;
    onChange: (value: string) => void;
    externalRef?: MutableRefObject<Quill | null> | null;
    showPhrasesButton?: boolean;
    plainTextOutput?: boolean;
};

export const useQuill = ({
    placeholder,
    id,
    signature,
    initialValue = '',
    onChange,
    externalRef,
    showPhrasesButton,
    plainTextOutput,
}: UseQuillOptions) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const lastValueRef = useRef<string>('');
    const lastHtmlRef = useRef<string>('');
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const log = useLogger();

    // Ref that holds the post-change logic; set inside the useEffect once
    // Quill is initialised so the closure captures the quill instance.
    const notifyChangeImplRef = useRef<() => void>(() => {});
    const notifyChange = useCallback(() => notifyChangeImplRef.current(), []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const editorDiv = document.createElement('div');
        if (id) editorDiv.id = id;

        container.appendChild(editorDiv);

        const undoHandler = () => {
            quill.history.undo();
        };

        const redoHandler = () => {
            quill.history.redo();
        };

        const quill = new Quill(editorDiv, {
            theme: 'snow',
            placeholder,
            bounds: container,
            formats: plainTextOutput
                ? ['mergeField', 'cursorPlaceholder']
                : [
                      'bold',
                      'underline',
                      'list',
                      'mergeField',
                      'cursorPlaceholder',
                  ],
            modules: {
                uploader: {
                    handler() {
                        // Prevent file uploads (images, etc.)
                    },
                },
                history: {
                    delay: 1000,
                    maxStack: 500,
                    userOnly: true,
                },
                toolbar: {
                    container: [
                        ...(plainTextOutput
                            ? []
                            : [
                                  ['bold', 'underline'],
                                  [{ list: 'ordered' }, { list: 'bullet' }],
                              ]),
                        ['undo', 'redo'],
                        ...(showPhrasesButton ? [['phrases']] : []),
                    ],
                    handlers: {
                        undo: undoHandler,
                        redo: redoHandler,
                        phrases: () => {}, // click handled externally via querySelector
                    },
                },
                clipboard: {
                    matchVisual: false,
                },
                keyboard: {
                    bindings: {
                        ...(plainTextOutput
                            ? {}
                            : {
                                  'list autofill': {
                                      prefix: /^\s*$/,
                                  },
                              }),
                        undo: {
                            key: 'Z',
                            ctrlKey: true,
                            handler: undoHandler,
                        },
                        redo: {
                            key: 'Y',
                            ctrlKey: true,
                            handler: redoHandler,
                        },
                    },
                },
            },
        });
        quillRef.current = quill;
        if (externalRef) externalRef.current = quill;

        const initialHtml = plainTextOutput
            ? plainTextToQuillHtml(initialValue)
            : initialValue || '';
        quill.root.innerHTML = initialHtml;
        lastHtmlRef.current = initialHtml;
        lastValueRef.current = initialValue || '';

        const undoButton = container.querySelector('.ql-undo');
        const redoButton = container.querySelector('.ql-redo');

        if (undoButton) {
            undoButton.innerHTML = '';
            undoButton.setAttribute('title', R.ToolbarUndoTooltip);
            const undoRoot = createRoot(undoButton);
            undoRoot.render(<UndoIcon />);
        }

        if (redoButton) {
            redoButton.innerHTML = '';
            redoButton.setAttribute('title', R.ToolbarRedoTooltip);
            const redoRoot = createRoot(redoButton);
            redoRoot.render(<RedoIcon />);
        }

        const phrasesButton = container.querySelector('.ql-phrases');
        if (phrasesButton) {
            phrasesButton.innerHTML = '';
            phrasesButton.setAttribute('title', R.ToolbarInsertPhraseTooltip);
            const phrasesRoot = createRoot(phrasesButton);
            phrasesRoot.render(<PlusIcon />);
        }

        const syncChange = () => {
            const html = quill.root.innerHTML;
            const wasEmpty = isEmptyHtml(lastHtmlRef.current);
            const isNowEmpty = isEmptyHtml(html);

            if (isNowEmpty) {
                log('Editor text is now empty');
            }

            if (
                wasEmpty &&
                !isNowEmpty &&
                signature &&
                !containsSignature(html, signature)
            ) {
                const selection = quill.getSelection();

                insertSignatureAtEnd(quill, signature);

                if (selection) {
                    quill.setSelection(
                        selection.index,
                        selection.length,
                        'silent',
                    );
                }
            }

            const updatedHtml = quill.root.innerHTML;
            lastHtmlRef.current = updatedHtml;
            const externalValue = plainTextOutput
                ? quillHtmlToPlainText(updatedHtml)
                : updatedHtml;
            lastValueRef.current = externalValue;
            onChangeRef.current(externalValue);
        };

        notifyChangeImplRef.current = syncChange;

        quill.on('text-change', (_delta, _oldDelta, source) => {
            if (source !== 'user') return;
            syncChange();
        });

        return () => {
            quillRef.current = null;
            if (externalRef) externalRef.current = null;
            container.innerHTML = '';
        };
    }, [placeholder, id, signature, plainTextOutput]);

    return {
        containerRef,
        quillRef,
        lastValueRef,
        notifyChange,
    };
};
